/**
 * Peripheral colour-therapy pulse — a very subtle full-viewport wash that
 * shifts colour in sync with the current breath phase. The eye barely reads
 * it directly; peripheral vision does the work, gently cueing "we're
 * breathing in / holding / breathing out" without ever competing with the
 * orb or the countdown.
 *
 * by David Hunt
 *
 * Design notes:
 *   • Warm amber on the inhale (expansion / opening)
 *   • Cool indigo on the hold  (stillness)
 *   • Soft sage on the exhale  (release, coolness)
 *   • `mix-blend-mode: soft-light` so the wash tints the scene rather than
 *     painting over it — reads as atmosphere, not a colour splash.
 *   • Peak opacity ≤ 0.14, and the fade duration is a fraction of the phase
 *     so the colour arrives with the breath rather than lagging behind.
 *   • AnimatePresence with `mode="wait"` cross-fades between phases via a
 *     new keyed motion.div, so the transition IS the breath.
 *   • `pointer-events-none` + `aria-hidden` — invisible to assistive tech.
 */

import React from "react";
import { motion, AnimatePresence } from "framer-motion";

const PHASE_STYLES = {
  inhale: { rgb: "255, 210, 160", peak: 0.14 }, // warm amber
  hold: { rgb: "160, 180, 220", peak: 0.08 }, // cool indigo
  exhale: { rgb: "180, 220, 200", peak: 0.12 }, // soft sage
  rest: { rgb: "160, 180, 220", peak: 0.05 }, // treated like a short hold
};

export const BreathColorPulse = ({ active, phase, seconds = 4 }) => {
  const label = (phase || "").toLowerCase();
  const style = PHASE_STYLES[label];
  if (!active || !style) return null;

  // Fade over a fraction of the phase so the colour is fully present in the
  // middle of the breath rather than lagging or landing after it ends.
  const dur = Math.max(1.2, seconds * 0.55);

  return (
    <div
      className="pointer-events-none fixed inset-0"
      aria-hidden="true"
      data-testid="breath-color-pulse"
      data-phase={label}
      data-active="true"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={label}
          className="absolute inset-0"
          style={{
            backgroundColor: `rgb(${style.rgb})`,
            mixBlendMode: "soft-light",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: style.peak }}
          exit={{ opacity: 0 }}
          transition={{ duration: dur, ease: "easeInOut" }}
        />
      </AnimatePresence>
    </div>
  );
};

export default BreathColorPulse;
